import { z } from 'zod';

/**
 * Skills: reusable instruction bundles in the open Agent Skills format (SKILL.md + optional files).
 * Parsing / zip handling lives in @af/skills; these are the wire + DB shapes.
 */
export const SKILL_STATUSES = ['draft', 'active', 'archived'] as const;
export const SkillStatus = z.enum(SKILL_STATUSES);
export type SkillStatus = z.infer<typeof SkillStatus>;

export const SKILL_SOURCES = ['manual', 'import', 'agent'] as const;
export const SkillSource = z.enum(SKILL_SOURCES);
export type SkillSource = z.infer<typeof SkillSource>;

/** Same trust vocabulary as agent memory: untrusted content never becomes active instructions on its own. */
export const SKILL_TRUSTS = ['user', 'agent', 'untrusted'] as const;
export const SkillTrust = z.enum(SKILL_TRUSTS);
export type SkillTrust = z.infer<typeof SkillTrust>;

/** `active` = is/was live, `proposed` = awaiting a human, `rejected` = a human declined it. */
export const SKILL_VERSION_STATES = ['active', 'proposed', 'rejected'] as const;
export const SkillVersionState = z.enum(SKILL_VERSION_STATES);
export type SkillVersionState = z.infer<typeof SkillVersionState>;

export const SkillFile = z.object({
  path: z.string(),
  content: z.string(),
  sha256: z.string(),
  size: z.number().int(),
});
export type SkillFile = z.infer<typeof SkillFile>;

/** An agent version's skill attachment; the live version of the skill is resolved at run start. */
export const SkillBinding = z.object({ skillId: z.string().uuid() });
export type SkillBinding = z.infer<typeof SkillBinding>;

export const SkillVersionDto = z.object({
  id: z.string(),
  version: z.number(),
  state: SkillVersionState,
  description: z.string(),
  body: z.string(),
  files: z.array(SkillFile),
  frontmatter: z.record(z.string(), z.unknown()),
  contentHash: z.string(),
  changelog: z.string().nullable(),
  createdBy: z.string().nullable(),
  /** The run an agent drafted this version from (null for human versions). */
  sourceRunId: z.string().nullable().default(null),
  createdAt: z.string(),
});
export type SkillVersionDto = z.infer<typeof SkillVersionDto>;

export const SkillDto = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: SkillStatus,
  source: SkillSource,
  trust: SkillTrust,
  authorAgentId: z.string().nullable(),
  /** Live version (null while only drafts exist). */
  currentVersion: z.number().nullable(),
  /** A newer proposed version waits for review. */
  hasProposal: z.boolean(),
  /** Agents whose current version binds this skill. */
  agentIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Version shown by default: the live one, else the newest proposal. Omitted on lists. */
  latest: SkillVersionDto.optional(),
});
export type SkillDto = z.infer<typeof SkillDto>;

const SkillFileInput = z.object({ path: z.string().max(300), content: z.string().max(1_000_000) });

/** Create a skill or add a version from SKILL.md text + extra files (validated by @af/skills). */
export const SkillInput = z.object({
  skillMd: z.string().min(1).max(200_000),
  files: z.array(SkillFileInput).max(64).default([]),
  changelog: z.string().max(500).optional(),
});
export type SkillInput = z.infer<typeof SkillInput>;

/** Import a zip (base64) in the open skill layout. */
export const SkillImportInput = z.object({
  zipBase64: z.string().min(1).max(2_000_000),
  changelog: z.string().max(500).optional(),
});
export type SkillImportInput = z.infer<typeof SkillImportInput>;

export const SkillExportDto = z.object({ filename: z.string(), zipBase64: z.string() });
export type SkillExportDto = z.infer<typeof SkillExportDto>;

/** Activate/reject a version (default: the newest proposal). Session-only: never over PAT/MCP. */
export const SkillReviewInput = z.object({ version: z.number().int().min(1).optional() });
export type SkillReviewInput = z.infer<typeof SkillReviewInput>;

export const SkillsQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  status: SkillStatus.optional(),
});

/**
 * A skill an agent drafted (or an edit it proposed) that waits for a human: the suggestion list on the
 * Skills page. `steps`/`similarRuns` are the deterministic evidence behind an automatic draft.
 */
export const SkillSuggestionDto = z.object({
  skillId: z.string(),
  name: z.string(),
  /** `new` = never live yet, `update` = a proposed change to a live skill. */
  kind: z.enum(['new', 'update']),
  trust: SkillTrust,
  /** Drafted by the platform's repetition detection (vs. an agent calling `skill_propose`). */
  auto: z.boolean(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  proposal: SkillVersionDto,
  /** The live version this would replace (updates only). */
  live: SkillVersionDto.nullable(),
  /** Tool sequence that repeated (automatic drafts only). */
  steps: z.array(z.string()).nullable(),
  /** How many similar runs the draft is based on. */
  similarRuns: z.number().int().nullable(),
  createdAt: z.string(),
});
export type SkillSuggestionDto = z.infer<typeof SkillSuggestionDto>;

/** Window of the skill usage numbers (`skill_uses`): what "not loaded lately" means on the Skills page. */
export const SKILL_USAGE_DAYS = 30;

/**
 * How much a skill is used and what it costs (deterministic, from `skill_uses`): how often its catalog line
 * was offered to a run, how often a run loaded it, and the catalog line's approximate prompt tokens per run.
 */
export const SkillUsageDto = z.object({
  skillId: z.string(),
  /** Runs that had the skill in their catalog in the last `SKILL_USAGE_DAYS` days. */
  exposures: z.number().int(),
  /** Runs that loaded it (`load_skill`) in the last `SKILL_USAGE_DAYS` days. */
  loads: z.number().int(),
  /** loads / exposures; null without exposures. */
  loadRate: z.number().nullable(),
  /** Last load ever (also before the window); null when never loaded. */
  lastLoadedAt: z.string().nullable(),
  /** Approximate tokens its catalog line adds to every run that offers it; null without a live version. */
  catalogTokens: z.number().int().nullable(),
});
export type SkillUsageDto = z.infer<typeof SkillUsageDto>;

/**
 * Add a suggestion: make the proposed version live, optionally with the human's edits applied first.
 * A never-live suggestion may also be renamed (the SKILL.md name wins). Session-only.
 */
export const SkillAcceptInput = z.object({
  version: z.number().int().min(1).optional(),
  edit: SkillInput.optional(),
});
export type SkillAcceptInput = z.infer<typeof SkillAcceptInput>;

/** Bind a live skill to a persistent agent (a new agent version, like attaching a flow). */
export const SkillAttachInput = z.object({ agentId: z.string().uuid() });
export type SkillAttachInput = z.infer<typeof SkillAttachInput>;
